import React, { Component } from 'react';
import { Form, Field } from 'react-final-form';
import { withApollo } from 'react-apollo';
import { ExternalLink } from 'react-feather';
import { Alert, AlertButton, Heading3, StackView, SubmitButton, Text } from 'localmed-core';

import style from './AppointmentInsuranceForm.scss';

import { UniversalContext } from '../../../_universal';
import LMForm, {
  handleMutation,
  Field as LMField,
  Input,
  FieldHint,
} from '../../../legacyCore/Form';
import MaskedInput from '../../../legacyCore/MaskedInput';
import toISODateString from '../../utils/toISODateString';
import validate from '../../utils/validate';
import ErrorMessage from '../ErrorMessage';
import TrackClick from '../TrackClick';
import { updatePatientCoverage } from './queries';

import { PATIENT_COVERAGE_VALIDATIONS } from './validate';

function getFieldErrors(meta) {
  if (!meta.touched || !meta.error) return false;
  return Array.isArray(meta.error) ? meta.error : [meta.error];
}

class AppointmentInsuranceForm extends Component {
  static contextType = UniversalContext;

  state = {
    formError: undefined,
    formSuccess: false,
    formSubmitting: false,
  };

  componentDidMount() {
    const { appointment } = this.props;
    const { provider } = appointment;
    this.context.trackEvent({
      event: 'Viewed Appointment Insurance Form',
      properties: {
        providerId: provider.id,
        providerDisplay: provider.display,
        officeId: provider.office.id,
        officeDisplay: provider.office.display,
      },
    });
  }

  onValidate = async (values) => {
    try {
      await validate(values, PATIENT_COVERAGE_VALIDATIONS, { cleanAttributes: false });
    } catch (error) {
      return error;
    }
    return undefined;
  };

  onSubmit = async (values, form) => {
    const { client, appointment } = this.props;

    const variables = {
      appointmentId: appointment.id,
      patientToken: appointment.patientToken,
      patientCoverage: [
        {
          insuranceIssuer: {
            id: appointment.patientCoverage[0].insuranceIssuer.id,
            display: appointment.patientCoverage[0].insuranceIssuer.display,
          },
          employer: {
            display: values.employer,
          },
          insurancePhone: values.insurancePhone,
          memberId: values.memberId,
          group: values.group,
          subscriberGivenName: values.subscriberGivenName,
          subscriberFamilyName: values.subscriberFamilyName,
          subscriberDateOfBirth: toISODateString(values.subscriberBirthDate),
        },
      ],
    };

    let response;
    try {
      this.setState({ formSubmitting: true });
      response = await handleMutation(
        client.mutate({
          mutation: updatePatientCoverage,
          variables,
        }),
        'updatePatientCoverage'
      );
    } catch (formError) {
      this.context.trackEvent({
        event: 'Submitted Appointment Insurance Form',
        properties: {
          isSuccessful: false,
          status: 500,
          message: formError,
          errors: [],
        },
      });
      this.setState({ formError, formSuccess: false, formSubmitting: false });
      return;
    }
    this.context.trackEvent({
      event: 'Submitted Appointment Insurance Form',
      properties: response,
    });
    form.change('serverErrors', response.errors);
    this.setState({
      formError: response.isSuccessful ? null : response.message,
      formSuccess: response.isSuccessful,
      formSubmitting: false,
    });
  };

  render() {
    const { formError, formSuccess, formSubmitting } = this.state;
    const { appointment } = this.props;
    const { practice } = appointment.provider;

    if (formSuccess) {
      return (
        <Alert tier="success" scrollIntoView>
          {practice.intakeFormUrl ? (
            <React.Fragment>
              <Heading3 color="inherit">
                We have sent your insurance information to {practice.display}.
              </Heading3>
              <Text marginBottom="sm">The next step is to fill out the check-in forms.</Text>
              <TrackClick
                event="Clicked Fill Out Check-in Forms"
                eventProperties={{ component: 'AppointmentInsuranceForm' }}
              >
                {(getTrackingProps) => (
                  <AlertButton
                    href={practice.intakeFormUrl || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<ExternalLink />}
                    {...getTrackingProps()}
                  >
                    Fill Out Check-in Forms
                  </AlertButton>
                )}
              </TrackClick>
            </React.Fragment>
          ) : (
            <p>We have sent your insurance information to {practice.display}.</p>
          )}
        </Alert>
      );
    }

    return (
      <Form
        onSubmit={this.onSubmit}
        validate={this.onValidate}
        render={({ handleSubmit, form, values }) => (
          <LMForm onSubmit={handleSubmit} gray data-qa="appointment-detail__insurance-form">
            <StackView spacing="sm">
              <StackView spacing="xxs">
                <Heading3 fontWeight="bold">Want to speed things up?</Heading3>
                <Text>
                  Providing your insurance up front will mean less paperwork when you get to your
                  appointment.
                </Text>
              </StackView>
              {formError !== undefined && (
                <ErrorMessage errorDetails={formError} scrollIntoView>
                  There has beeen an error submitting the form.
                </ErrorMessage>
              )}
              <div className={style['insurance-form']}>
                <div className={style['insurance-form__section']}>
                  <Field
                    name="insurancePhone"
                    render={({ input, meta }) => (
                      <LMField
                        label="Insurance Phone Number"
                        name="insurancePhone"
                        htmlFor="insurancePhone"
                        errors={getFieldErrors(meta)}
                      >
                        <Input
                          type="tel"
                          name="insurancePhone"
                          placeholder="(###) ###-####"
                          autoComplete="off"
                          valid={!(meta.error && meta.touched)}
                          {...input}
                          onBlur={() => {
                            form.blur('insurancePhone');
                            form.change('serverErrors', {
                              ...(values ? values.serverErrors : {}),
                              'patientCoverage.insurancePhone': null,
                            });
                          }}
                        />
                        <FieldHint>
                          This is found on your insurance card. If there are multiple numbers, enter
                          the <em>Customer Support</em> number.
                        </FieldHint>
                      </LMField>
                    )}
                  />

                  <Field
                    name="memberId"
                    render={({ input, meta }) => (
                      <LMField
                        label="Member ID"
                        name="memberId"
                        htmlFor="memberId"
                        errors={getFieldErrors(meta)}
                      >
                        <Input
                          type="text"
                          name="memberId"
                          autoComplete="off"
                          valid={!(meta.error && meta.touched)}
                          {...input}
                        />
                      </LMField>
                    )}
                  />

                  <Field
                    name="group"
                    render={({ input, meta }) => (
                      <LMField
                        label="Group Number"
                        name="group"
                        htmlFor="group"
                        errors={getFieldErrors(meta)}
                      >
                        <Input type="text" name="group" autoComplete="off" {...input} />
                      </LMField>
                    )}
                  />

                  <Field
                    name="employer"
                    render={({ input, meta }) => (
                      <LMField
                        label="Employer Name"
                        name="employer"
                        htmlFor="employer"
                        errors={getFieldErrors(meta)}
                      >
                        <Input type="text" name="employer" autoComplete="off" {...input} />
                      </LMField>
                    )}
                  />
                </div>

                <div className={style['insurance-form__section']}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="subscriberGivenName" className="font-size-xs">
                    Policy Holder’s Name
                  </label>

                  <div className={style['insurance-form__sideways']}>
                    <Field
                      name="subscriberGivenName"
                      render={({ input, meta }) => (
                        <LMField
                          name="subscriberGivenName"
                          htmlFor="subscriberGivenName"
                          errors={getFieldErrors(meta)}
                        >
                          <Input
                            type="text"
                            id="subscriberGivenName"
                            name="subscriberGivenName"
                            autoComplete="off"
                            placeholder="First"
                            {...input}
                          />
                        </LMField>
                      )}
                    />

                    <Field
                      name="subscriberFamilyName"
                      render={({ input, meta }) => (
                        <LMField
                          name="subscriberFamilyName"
                          htmlFor="subscriberFamilyName"
                          errors={getFieldErrors(meta)}
                        >
                          <Input
                            type="text"
                            name="subscriberFamilyName"
                            autoComplete="off"
                            placeholder="Last"
                            {...input}
                          />
                        </LMField>
                      )}
                    />
                  </div>

                  <Field
                    name="subscriberBirthDate"
                    render={({ input, meta }) => (
                      <LMField
                        label="Policy Holder’s Birthday"
                        name="subscriberBirthDate"
                        htmlFor="subscriberBirthDate"
                        errors={getFieldErrors(meta)}
                      >
                        <MaskedInput
                          type="text"
                          name="subscriberBirthDate"
                          autoComplete="off"
                          mask="11/11/1111"
                          placeholder="MM/DD/YYYY"
                          {...input}
                        />
                      </LMField>
                    )}
                  />

                  <SubmitButton
                    tier="alert"
                    full
                    defaultText="Send Your Insurance Information"
                    submittingText="Sending"
                    submitting={formSubmitting}
                    className={style['insurance-form__button']}
                  />
                </div>
              </div>
            </StackView>
          </LMForm>
        )}
      />
    );
  }
}

const AppointmentInsuranceFormWithApollo = withApollo(AppointmentInsuranceForm);

export default AppointmentInsuranceFormWithApollo;
